<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">Promotions ({{ totalRows }})</h1>
        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['product_promotions', 'ProductPromotion']" label="Promotions" />
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            @click="filtersVisible = !filtersVisible"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>
          <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouvelle promotion</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 mb-3">
        {{ errorMessage }}
      </div>

      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label small mb-1">Statut</label>
              <PrimevueDropdown
                v-model="filters.status"
                :options="statusFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
                placeholder="Tous"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-1">Source</label>
              <PrimevueDropdown
                v-model="filters.source"
                :options="sourceFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
                placeholder="Toutes"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-1">Début</label>
              <input v-model="filters.date_from" type="date" class="form-control" />
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-1">Fin</label>
              <input v-model="filters.date_to" type="date" class="form-control" />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchPromotions">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="promotionsResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkDeactivatePromotions"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectPromotion"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="promotions.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher produit, SKU ou note..."
        empty-text="Aucune promotion trouvée."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchPromotions"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkDeactivatePromotions"
          >
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-product_name="{ row }">
          <div class="d-flex flex-column">
            <span class="fw-semibold">{{ row.product_name || 'Produit' }}</span>
            <small class="text-muted">{{ row.product_sku || '-' }}</small>
          </div>
        </template>

        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(promotionStatus(row))">
            {{ statusLabel(promotionStatus(row)) }}
          </span>
        </template>

        <template #cell-source="{ row }">
          <span class="badge" :class="row.source === 'cashier' ? 'bg-info text-dark' : 'bg-primary'">
            {{ sourceLabel(row.source) }}
          </span>
        </template>

        <template #cell-regular_sale_price="{ row }">
          <span class="text-muted">{{ $format.currency(row.regular_sale_price || 0) }}</span>
        </template>

        <template #cell-promotion_price="{ row }">
          <span class="fw-semibold text-success">{{ $format.currency(row.promotion_price || 0) }}</span>
        </template>

        <template #cell-starts_at="{ row }">
          {{ row.starts_at ? $format.datetime(row.starts_at) : '—' }}
        </template>

        <template #cell-ends_at="{ row }">
          {{ row.ends_at ? $format.datetime(row.ends_at) : '—' }}
        </template>

        <template #cell-actions="{ row }">
          <div class="d-flex justify-content-end gap-1">
            <button type="button" class="btn btn-outline-primary btn-sm btn-action-icon" :title="isExpiredPromotion(row) ? expiredPromotionMessage : 'Modifier'" :disabled="isExpiredPromotion(row)" @click.stop="openEditDialog(row)">
              <i class="fa fa-edit"></i>
            </button>
            <AppLoadingButton
              type="button"
              class="btn btn-outline-danger btn-sm btn-action-icon"
              title="Désactiver"
              :loading="isDeactivatingPromotion(row)"
              :disabled="!canSelectPromotion(row)"
              loading-label=""
              @click.stop="deactivatePromotion(row)"
            >
              <i class="fa fa-ban"></i>
            </AppLoadingButton>
          </div>
        </template>
      </BootstrapDataTable>

      <PrimevueDialog v-model:visible="dialogVisible" :modal="true" :closable="false" :style="{ width: '620px', maxWidth: '95vw' }">
        <template #header>
          <h5 class="modal-title mb-0">
            {{ dialogMode === 'create' ? 'Nouvelle promotion' : 'Modifier la promotion' }}
          </h5>
        </template>

        <form @submit.prevent="savePromotion">
          <div v-if="editingExpiredPromotion" class="alert alert-warning py-2" role="alert">
            {{ expiredPromotionMessage }}
          </div>
          <div v-if="formError" class="alert alert-danger py-2">
            {{ formError }}
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Produit</label>
            <PrimevueDropdown
              v-model="form.product_id"
              :options="productOptions"
              optionLabel="label"
              optionValue="id"
              class="w-100"
              filter
              placeholder="Sélectionner un produit"
              :loading="isLoadingProducts"
              :disabled="editingExpiredPromotion"
            />
            <small v-if="selectedProduct" class="text-muted">
              Prix normal: {{ $format.currency(selectedProduct.sale_price || 0) }}
            </small>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Prix promotionnel TTC</label>
            <PrimevueInputNumber
              v-model="form.promotion_price"
              class="w-100"
              mode="decimal"
              :min="0"
              :maxFractionDigits="2"
              :disabled="editingExpiredPromotion"
            />
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">Début</label>
              <input v-model="form.starts_at" :disabled="editingExpiredPromotion" type="datetime-local" class="form-control" required />
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">Fin</label>
              <input v-model="form.ends_at" :disabled="editingExpiredPromotion" type="datetime-local" class="form-control" required />
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Note</label>
            <PrimevueTextarea v-model="form.notes" :disabled="editingExpiredPromotion" class="w-100" rows="3" autoResize />
          </div>

          <div class="form-check mb-3">
            <PrimevueCheckbox v-model="form.is_active" :disabled="editingExpiredPromotion" binary inputId="promotionActive" />
            <label for="promotionActive" class="form-check-label ms-2">Promotion active</label>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary" :disabled="isSaving" @click="closeDialog">
              <i class="fa fa-xmark me-1" aria-hidden="true"></i>
              Annuler
            </button>
            <AppLoadingButton
              type="submit"
              class="btn btn-primary"
              :loading="isSaving"
              :disabled="editingExpiredPromotion"
              loading-label="Enregistrement..."
            >
              <i class="fa fa-save me-1" aria-hidden="true"></i>
              Enregistrer
            </AppLoadingButton>
          </div>
        </form>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import { countActiveFilters } from '@/support/moduleFilters';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

const DEFAULT_FILTERS = {
  search: '',
  status: '',
  source: '',
  date_from: '',
  date_to: '',
};

const toLocalDateTimeValue = (value = new Date()) => {
  const date = value ? new Date(value) : new Date();
  const pad = (number) => String(number).padStart(2, '0');

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const defaultPromotionForm = () => {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 0, 0);

  return {
    product_id: null,
    promotion_price: 0,
    starts_at: toLocalDateTimeValue(now),
    ends_at: toLocalDateTimeValue(end),
    notes: '',
    is_active: true,
  };
};

export default {
  name: 'PromotionsView',
  mixins: [bulkActionViewMixin],
  data() {
    return {
      promotionsResponse: null,
      currentTime: Date.now(),
      expiryTimer: null,
      expiredPromotionMessage: 'Une promotion expirée ne peut plus être modifiée ou désactivée.',
      productOptions: [],
      isLoading: false,
      isLoadingProducts: false,
      isSaving: false,
      deactivatingPromotionId: null,
      errorMessage: '',
      formError: '',
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'starts_at',
        sortDir: 'desc',
      },
      tableColumns: [
        { key: 'product_name', label: 'Produit', sortable: false },
        { key: 'status', label: 'Statut', sortable: false, class: 'text-center' },
        { key: 'source', label: 'Source', sortable: false, class: 'text-center' },
        { key: 'regular_sale_price', label: 'Prix normal', class: 'text-end' },
        { key: 'promotion_price', label: 'Prix promo', sortable: true, class: 'text-end' },
        { key: 'starts_at', label: 'Début', sortable: true },
        { key: 'ends_at', label: 'Fin', sortable: true },
        { key: 'creator_name', label: 'Créé par' },
        { key: 'actions', label: '', class: 'text-end', style: 'width: 110px;' },
      ],
      statusFilterOptions: [
        { label: 'Tous', value: '' },
        { label: 'Programmée', value: 'scheduled' },
        { label: 'Active', value: 'active' },
        { label: 'Expirée', value: 'expired' },
        { label: 'Désactivée', value: 'disabled' },
      ],
      sourceFilterOptions: [
        { label: 'Toutes', value: '' },
        { label: 'Planifiée', value: 'planned' },
        { label: 'Caisse', value: 'cashier' },
      ],
      dialogVisible: false,
      dialogMode: 'create',
      selectedPromotion: null,
      form: defaultPromotionForm(),
    };
  },
  computed: {
    editingExpiredPromotion() {
      return this.dialogMode === 'edit' && this.isExpiredPromotion(this.selectedPromotion);
    },
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    canBulkDeactivatePromotions() {
      return hasPermission(this.currentUser, 'promotions.delete');
    },
    totalRows() {
      return this.promotionsResponse?.meta?.pagination?.total || 0;
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    selectedProduct() {
      return this.productOptions.find((product) => Number(product.id) === Number(this.form.product_id)) || null;
    },
  },
  mounted() {
    // Une promotion peut expirer pendant que son formulaire ou sa sélection reste ouvert.
    this.expiryTimer = window.setInterval(() => {
      this.currentTime = Date.now();
      this.removeExpiredSelections();
    }, 1000);
    this.fetchProducts();
    this.fetchPromotions();
  },
  beforeUnmount() {
    window.clearInterval(this.expiryTimer);
  },
  methods: {
    isExpiredPromotion(row) {
      if (!row) return false;
      return row.is_expired === true || row.status === 'expired'
        || Boolean(row.ends_at && new Date(row.ends_at).getTime() < this.currentTime);
    },
    promotionStatus(row) {
      return this.isExpiredPromotion(row) ? 'expired' : row.status;
    },
    removeExpiredSelections() {
      if (!this.bulkSelectedIds.length) return;
      const rows = this.promotionsResponse?.data || [];
      const expiredIds = new Set(rows.filter((row) => this.isExpiredPromotion(row)).map((row) => String(row.id)));
      if (expiredIds.size) {
        const remainingIds = this.bulkSelectedIds.filter((id) => !expiredIds.has(String(id)));
        if (remainingIds.length !== this.bulkSelectedIds.length) this.bulkSelectedIds = remainingIds;
      }
    },
    canSelectPromotion(row) {
      return [true, 1, '1', 'true'].includes(row?.is_active) && !this.isExpiredPromotion(row);
    },
    async bulkDeactivatePromotions() {
      this.currentTime = Date.now();
      this.removeExpiredSelections();
      await this.performBulkAction({
        url: '/product-promotions/bulk/deactivate',
        method: 'patch',
        itemLabel: 'promotion',
        confirmation: (count) => ({
          header: 'Désactiver les promotions',
          message: `Désactiver les ${count} promotions sélectionnées ?`,
          acceptLabel: `Désactiver ${count}`,
          acceptClass: 'p-button-danger',
        }),
        refresh: () => this.fetchPromotions(),
      });
    },

    async fetchProducts() {
      this.isLoadingProducts = true;

      try {
        const response = await this.$api.get('/products', {
          params: { limit: 100, sort_by: 'name', sort_dir: 'asc' },
        });
        const rows = response.data.data || [];
        this.productOptions = rows.map((product) => ({
          ...product,
          label: `${product.name} · ${product.sku || 'sans SKU'}`,
        }));
      } catch (error) {
        console.error('Erreur chargement produits', error);
        this.productOptions = [];
      } finally {
        this.isLoadingProducts = false;
      }
    },
    async fetchPromotions() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.get('/product-promotions', {
          params: {
            ...this.filters,
            page: this.table.page,
            per_page: this.table.perPage,
            sort_by: this.table.sortBy,
            sort_dir: this.table.sortDir,
          },
        });
        this.promotionsResponse = response.data;
      } catch (error) {
        console.error('Erreur chargement promotions', error);
        this.errorMessage = 'Impossible de charger les promotions.';
        this.promotionsResponse = null;
      } finally {
        this.isLoading = false;
      }
    },
    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchPromotions();
    },
    handleTableSearch() {
      this.table.page = 1;
      this.fetchPromotions();
    },
    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchPromotions();
    },
    handlePageChange(page) {
      this.table.page = page;
      this.fetchPromotions();
    },
    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchPromotions();
    },
    openCreateDialog() {
      this.dialogMode = 'create';
      this.selectedPromotion = null;
      this.form = defaultPromotionForm();
      this.formError = '';
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.currentTime = Date.now();
      if (this.isExpiredPromotion(row)) {
        this.errorMessage = this.expiredPromotionMessage;
        return;
      }
      this.dialogMode = 'edit';
      this.selectedPromotion = row;
      this.form = {
        product_id: row.product_id,
        promotion_price: Number(row.promotion_price || 0),
        starts_at: this.toLocalDateTime(row.starts_at),
        ends_at: this.toLocalDateTime(row.ends_at),
        notes: row.notes || '',
        is_active: [true, 1, '1', 'true'].includes(row.is_active),
      };
      this.formError = '';
      this.dialogVisible = true;
    },
    closeDialog() {
      if (this.isSaving) return;
      this.dialogVisible = false;
    },
    async savePromotion() {
      if (this.isSaving) return;

      this.currentTime = Date.now();
      // La date chargée reste la référence, même si une prolongation est saisie dans le formulaire.
      if (this.editingExpiredPromotion) {
        this.formError = this.expiredPromotionMessage;
        return;
      }
      this.formError = '';
      this.isSaving = true;

      try {
        const payload = {
          product_id: this.form.product_id,
          promotion_price: Number(this.form.promotion_price || 0),
          starts_at: this.form.starts_at,
          ends_at: this.form.ends_at,
          is_active: this.form.is_active,
          notes: this.form.notes || null,
        };

        if (this.dialogMode === 'edit' && this.selectedPromotion?.id) {
          await this.$api.put(`/product-promotions/${this.selectedPromotion.id}`, payload);
        } else {
          await this.$api.post('/product-promotions', payload);
        }

        this.dialogVisible = false;
        await this.fetchPromotions();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.formError = errors ? Object.values(errors).flat().join(' ') : 'Impossible d’enregistrer la promotion.';
      } finally {
        this.isSaving = false;
      }
    },
    async deactivatePromotion(row) {
      this.currentTime = Date.now();
      if (this.isDeactivatingPromotion(row) || !this.canSelectPromotion(row)) return;

      this.deactivatingPromotionId = row.id;

      if (!(await this.$confirmAction(`Désactiver la promotion de "${row.product_name}" ?`))) {
        this.deactivatingPromotionId = null;
        return;
      }

      try {
        this.currentTime = Date.now();
        if (this.isExpiredPromotion(row)) {
          this.errorMessage = this.expiredPromotionMessage;
          return;
        }
        await this.$api.delete(`/product-promotions/${row.id}`);
        await this.fetchPromotions();
      } catch (error) {
        console.error('Erreur désactivation promotion', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Impossible de désactiver la promotion.';
      } finally {
        this.deactivatingPromotionId = null;
      }
    },
    isDeactivatingPromotion(row) {
      return Number(this.deactivatingPromotionId || 0) === Number(row?.id || 0);
    },
    statusLabel(status) {
      return {
        scheduled: 'Programmée',
        active: 'Active',
        expired: 'Expirée',
        disabled: 'Désactivée',
      }[status] || status || '—';
    },
    statusClass(status) {
      return {
        scheduled: 'bg-warning text-dark',
        active: 'bg-success',
        expired: 'bg-secondary',
        disabled: 'bg-light text-dark border',
      }[status] || 'bg-secondary';
    },
    sourceLabel(source) {
      return source === 'cashier' ? 'Caisse' : 'Planifiée';
    },
    toLocalDateTime(value) {
      return toLocalDateTimeValue(value);
    },
  },
};
</script>
